/**
 * User Settings Hook — reads/writes localStorage.userSettings
 *
 * Shares the same localStorage key as AngularJS ($rootScope.G_SETTINGS).
 * In embedded mode, both AngularJS and React read/write the same key.
 *
 * Settings structure (G_SETTINGS):
 *   { theme, loadingSpinner, showBidIds, showRecordKeys, showDashKeys }
 */

import { useState, useCallback } from 'react';
import { useAppGlobalsStore } from '@/store';

// ── Types ────────────────────────────────────────────────────────────────────

export interface UserSettings {
  theme?: string;
  loadingSpinner?: string;
  showBidIds?: boolean;
  showRecordKeys?: boolean;
  /** Matches AngularJS G_SETTINGS.showDashKeys (not "showDashboardKeys") */
  showDashKeys?: boolean;
}

export interface ThemeDefinition {
  name: string;
  primary: string;
  secondary: string;
}

export interface SpinnerDefinition {
  name: string;
  filename: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'userSettings';

export const THEMES: ThemeDefinition[] = [
  { name: 'default', primary: '#0075d6', secondary: '#53b50a' },
  { name: 'gold', primary: '#998800', secondary: '#1192A6' },
  { name: 'green', primary: '#58B50A', secondary: '#0075d6' },
  { name: 'red', primary: '#980000', secondary: '#CB4814' },
  { name: 'regal', primary: '#4169e1', secondary: '#b69133' },
  { name: 'bubblegum', primary: '#ec2072', secondary: '#7c20f6' },
  { name: 'high contrast', primary: '#222222', secondary: '#999999' },
];

export const SPINNERS: SpinnerDefinition[] = [
  { name: 'Default', filename: 'safeview.gif' },
  { name: 'Safeguard Globe', filename: 'globe.GIF' },
  { name: 'Stacked Building', filename: 'building.GIF' },
  { name: 'Flower', filename: 'flower.GIF' },
  { name: 'Kitkat', filename: 'kitty.GIF' },
  { name: 'Pac-Man', filename: 'pacman.GIF' },
  { name: 'Squares', filename: 'squares.GIF' },
  { name: '3d Gears', filename: 'gears.GIF' },
  { name: 'Airplane', filename: 'airplane.GIF' },
  { name: 'Rubiks Cube', filename: 'cube.GIF' },
  { name: 'Panda', filename: 'panda.GIF' },
  { name: 'Batter Up', filename: 'baseball.GIF' },
  { name: 'Brawl', filename: 'brawl.GIF' },
  { name: 'Octopus', filename: 'octopus.gif' },
  { name: 'Unicycle', filename: 'frog.GIF' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function hexToRgbChannels(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return `${parseInt(h.slice(0, 2), 16)} ${parseInt(h.slice(2, 4), 16)} ${parseInt(h.slice(4, 6), 16)}`;
}

function readSettings(): UserSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UserSettings;
  } catch {
    return {};
  }
}

function writeSettings(settings: UserSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable
  }
}

/** Apply theme CSS variables to the document root. */
function applyThemeCssVars(theme: ThemeDefinition): void {
  document.documentElement.style.setProperty(
    '--theme-primary-rgb',
    hexToRgbChannels(theme.primary)
  );
  document.documentElement.style.setProperty(
    '--theme-secondary-rgb',
    hexToRgbChannels(theme.secondary)
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useUserSettings() {
  const [settings, setSettingsState] = useState<UserSettings>(readSettings);

  const updateSettings = useCallback((patch: Partial<UserSettings>) => {
    setSettingsState((prev) => {
      const next = { ...prev, ...patch };
      writeSettings(next);
      // Sync to app globals store (which dual-writes to $rootScope in embedded mode)
      // so StandaloneSpinner and other consumers read the updated G_SETTINGS reactively.
      useAppGlobalsStore.getState().setSettings(next);
      return next;
    });
  }, []);

  const setTheme = useCallback(
    (themeName: string) => {
      const theme = THEMES.find((t) => t.name === themeName);
      if (!theme) return;
      applyThemeCssVars(theme);
      updateSettings({ theme: themeName });
    },
    [updateSettings]
  );

  const setSpinner = useCallback(
    (filename: string) => {
      updateSettings({ loadingSpinner: filename });
    },
    [updateSettings]
  );

  const toggleShowBidIds = useCallback(
    () => updateSettings({ showBidIds: !settings.showBidIds }),
    [settings.showBidIds, updateSettings]
  );

  const toggleShowRecordKeys = useCallback(
    () => updateSettings({ showRecordKeys: !settings.showRecordKeys }),
    [settings.showRecordKeys, updateSettings]
  );

  const toggleShowDashboardKeys = useCallback(
    () => updateSettings({ showDashKeys: !settings.showDashKeys }),
    [settings.showDashKeys, updateSettings]
  );

  return {
    settings,
    themes: THEMES,
    spinners: SPINNERS,
    setTheme,
    setSpinner,
    toggleShowBidIds,
    toggleShowRecordKeys,
    toggleShowDashboardKeys,
  };
}
